import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Star, Quote, Play } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface TestimonialData {
  id: string;
  name: string;
  position: string;
  company: string;
  location: string;
  avatar: string;
  rating: number;
  testimonial: {
    de: string;
    en: string;
    fr: string;
    it: string;
  };
  videoUrl?: string;
  projectType: string;
  results: {
    de: string;
    en: string;
    fr: string;
    it: string;
  };
}

const SwissTestimonials: React.FC = () => {
  const { currentLanguage } = useLanguage();

  const testimonials: TestimonialData[] = [
    {
      id: 'zurich-pharma',
      name: 'Dr. Andrea Müller',
      position: 'Marketing Director',
      company: 'SwissPharma AG',
      location: 'Zürich, CH',
      avatar: '/lovable-uploads/70f2557d-0baf-45f1-a751-5f50715678b3.png',
      rating: 5,
      testimonial: {
        de: 'Future Level hat unsere Produktvideos revolutioniert. Die AI-gesteuerte Personalisierung führte zu 340% mehr Engagement und signifikant höheren Conversion-Raten.',
        en: 'Future Level revolutionized our product videos. The AI-driven personalization led to 340% more engagement and significantly higher conversion rates.',
        fr: 'Future Level a révolutionné nos vidéos produits. La personnalisation basée sur l\'IA a conduit à 340% d\'engagement en plus et des taux de conversion significativement plus élevés.',
        it: 'Future Level ha rivoluzionato i nostri video prodotto. La personalizzazione basata sull\'IA ha portato al 340% di engagement in più e tassi di conversione significativamente più alti.'
      },
      videoUrl: '#',
      projectType: 'AI Product Videos',
      results: {
        de: '+340% Engagement, +180% Conversions',
        en: '+340% Engagement, +180% Conversions', 
        fr: '+340% Engagement, +180% Conversions',
        it: '+340% Engagement, +180% Conversions'
      }
    },
    {
      id: 'geneva-luxury',
      name: 'Pierre Dubois',
      position: 'CEO',
      company: 'Luxe Geneva SA',
      location: 'Genève, CH',
      avatar: '/lovable-uploads/a72a05cf-e6d5-467d-9697-09456622f3c3.png',
      rating: 5,
      testimonial: {
        de: 'Das multilingual SEO-System von Future Level brachte uns in 4 Sprachen auf Seite 1. Unser organischer Traffic stieg um 450% in nur 6 Monaten.',
        en: 'Future Level\'s multilingual SEO system got us to page 1 in 4 languages. Our organic traffic increased by 450% in just 6 months.',
        fr: 'Le système SEO multilingue de Future Level nous a mis en page 1 dans 4 langues. Notre trafic organique a augmenté de 450% en seulement 6 mois.',
        it: 'Il sistema SEO multilingue di Future Level ci ha portato in prima pagina in 4 lingue. Il nostro traffico organico è aumentato del 450% in soli 6 mesi.'
      },
      projectType: 'Multilingual SEO',
      results: {
        de: '+450% Organischer Traffic, #1 Rankings',
        en: '+450% Organic Traffic, #1 Rankings',
        fr: '+450% Trafic Organique, #1 Rankings',
        it: '+450% Traffico Organico, #1 Rankings'
      }
    },
    {
      id: 'basel-tech',
      name: 'Marco Schneider',
      position: 'CTO',
      company: 'TechStart Basel',
      location: 'Basel, CH',
      avatar: '/lovable-uploads/70f2557d-0baf-45f1-a751-5f50715678b3.png',
      rating: 5,
      testimonial: {
        de: 'Die UX/UI-Redesign von Future Level steigerte unsere App-Retention um 280%. Schweizer Präzision trifft auf AI-Innovation.',
        en: 'Future Level\'s UX/UI redesign increased our app retention by 280%. Swiss precision meets AI innovation.',
        fr: 'Le redesign UX/UI de Future Level a augmenté notre rétention d\'app de 280%. La précision suisse rencontre l\'innovation IA.',
        it: 'Il redesign UX/UI di Future Level ha aumentato la nostra retention dell\'app del 280%. La precisione svizzera incontra l\'innovazione AI.'
      },
      projectType: 'UX/UI Design',
      results: {
        de: '+280% App Retention, +95% User Satisfaction',
        en: '+280% App Retention, +95% User Satisfaction',
        fr: '+280% Rétention App, +95% Satisfaction Utilisateur',
        it: '+280% Retention App, +95% Soddisfazione Utente'
      }
    }
  ];

  const translations = {
    de: {
      title: 'Schweizer Unternehmen vertrauen uns',
      subtitle: 'Echte Ergebnisse von echten Schweizer Kunden',
      watchVideo: 'Video ansehen',
      verified: 'Verifiziert'
    },
    en: {
      title: 'Swiss Companies Trust Us',
      subtitle: 'Real results from real Swiss customers',
      watchVideo: 'Watch Video',
      verified: 'Verified'
    },
    fr: {
      title: 'Les entreprises suisses nous font confiance',
      subtitle: 'De vrais résultats de vrais clients suisses',
      watchVideo: 'Regarder la vidéo',
      verified: 'Vérifié'
    },
    it: {
      title: 'Le aziende svizzere si fidano di noi',
      subtitle: 'Risultati reali da veri clienti svizzeri',
      watchVideo: 'Guarda il video',
      verified: 'Verificato'
    }
  };

  const t = translations[currentLanguage as keyof typeof translations];

  return (
    <section className="py-20 bg-gradient-to-b from-background via-background/95 to-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
            {t.title}
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="neo-card h-full group hover:scale-105 transition-all duration-300">
                <CardContent className="p-6">
                  {/* Header with avatar and company info */}
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="relative">
                      <img
                        src={testimonial.avatar}
                        alt={testimonial.name}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                      <Badge
                        variant="secondary"
                        className="absolute -bottom-2 -right-2 text-xs bg-green-500/20 text-green-400 border-green-500/30"
                      >
                        {t.verified}
                      </Badge>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground">{testimonial.name}</h3>
                      <p className="text-sm text-muted-foreground">{testimonial.position}</p>
                      <p className="text-sm font-medium text-primary">{testimonial.company}</p>
                      <p className="text-xs text-muted-foreground">{testimonial.location}</p>
                    </div>
                  </div>

                  {/* Rating */}
                  <div className="flex items-center space-x-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>

                  {/* Quote */}
                  <div className="relative mb-6">
                    <Quote className="absolute -top-2 -left-2 w-6 h-6 text-primary/30" />
                    <p className="text-foreground/90 italic pl-4 leading-relaxed">
                      {testimonial.testimonial[currentLanguage as keyof typeof testimonial.testimonial]}
                    </p>
                  </div>

                  {/* Project type and results */}
                  <div className="space-y-3">
                    <Badge variant="outline" className="text-xs">
                      {testimonial.projectType}
                    </Badge>
                    <div className="bg-primary/10 rounded-lg p-3">
                      <p className="text-sm font-semibold text-primary">
                        {testimonial.results[currentLanguage as keyof typeof testimonial.results]}
                      </p>
                    </div>
                  </div>

                  {/* Video CTA */}
                  {testimonial.videoUrl && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <button
                        onClick={() => {/* TODO: Open video modal */}}
                        className="flex items-center space-x-2 text-primary hover:text-primary-glow transition-colors"
                      >
                        <Play className="w-4 h-4" />
                        <span className="text-sm font-medium">{t.watchVideo}</span>
                      </button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SwissTestimonials;